import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, Pagination, Box, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import WikipediaViewer from '../../components/WikipediaViewer';
import Expandable from '../../components/common/Expandable';
import { getMovieByGenre, getMovieByCompany, getMovieByCollection, getMovieByPerson, getBasicMovieStatuses } from '../../services/movieApi';
import { Movie } from '../../types/Movie';
import LoadingPlaceholder from '../../components/LoadingPlaceholder';
import MovieCard from '../../components/MovieCard';
import { Helmet } from 'react-helmet-async';
import AdvertisementResponsive from '../../components/advertisement/Ad_GA_res';
import Advertisement from '../../components/advertisement/Ad_wide_728';
import RectangleAdvertisement from '../../components/advertisement/Ad_rectangle_300';

const ITEMS_PER_PAGE = 30;

const MovieListByCategory: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [japaneseName, setJapaneseName] = useState<string | null>(null);
  const [englishName, setEnglishName] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [movieStatuses, setMovieStatuses] = useState<{ [key: number]: number | null }>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [wikiSummary, setWikiSummary] = useState<string | null>(null);
  const [wikiOpen, setWikiOpen] = useState(false);
  const [hasWikiData, setHasWikiData] = useState(false);
  const [wikiLoading, setWikiLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (!isLoggedIn || movies.length === 0) return;
      const movieIds = movies.map(m => m.movie_id);
      const statuses = await getBasicMovieStatuses(movieIds);
      setMovieStatuses(statuses);
    };
    fetchStatuses();
  }, [movies, isLoggedIn]);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page') || '1', 10);

  useEffect(() => {
    const fetchMovies = async () => {
      setLoading(true);
      try {
        if (!id) throw new Error('Invalid ID');
        let response;
        switch (type) {
          case 'genre':
            response = await getMovieByGenre(id, currentPage);
            setJapaneseName(response.genre_name_ja || null);
            setEnglishName(response.genre_name);
            break;
          case 'company':
            response = await getMovieByCompany(id, currentPage);
            setJapaneseName(response.company_name_ja || null);
            setEnglishName(response.company_name);
            break;
          case 'collection':
            response = await getMovieByCollection(id, currentPage);
            setJapaneseName((response.collection_name_ja || '').replace(/\s*(Collection|シリーズ)\s*$/i, '').trim() || null);
            setEnglishName((response.collection_name || '').replace(/\s*(Collection|シリーズ)\s*$/i, '').trim());
            break;
          case 'person':
            response = await getMovieByPerson(id, currentPage);
            setJapaneseName(response.person_name_ja || null);
            setEnglishName(response.person_name);
            break;
          default:
            throw new Error('Invalid category type');
        }
        setMovies(response.results || []);
        setTotalCount(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / ITEMS_PER_PAGE));
      } catch (error) {
        console.error('映画一覧の取得に失敗しました', error);
        setMovies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMovies();
  }, [id, type, currentPage]);

  useEffect(() => {
    const searchName = japaneseName || englishName;
    if (!searchName) return;
    setWikiLoading(true);
    const fetchWikiSummary = async () => {
      try {
        const cachedData = localStorage.getItem(`wiki_summary_${searchName}`);
        if (cachedData) {
          const { summary, timestamp } = JSON.parse(cachedData);
          if (new Date().getTime() - timestamp < 24 * 60 * 60 * 1000) {
            setWikiSummary(summary);
            setHasWikiData(true);
            setWikiLoading(false);
            return;
          }
        }
        const response = await fetch(`https://ja.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(searchName)}`);
        const data = await response.json();
        if (data.extract) {
          setWikiSummary(data.extract);
          setHasWikiData(true);
          localStorage.setItem(`wiki_summary_${searchName}`, JSON.stringify({
            summary: data.extract,
            timestamp: new Date().getTime()
          }));
        } else {
          setHasWikiData(false);
        }
      } catch (error) {
        console.error('Wikipedia APIからのデータ取得に失敗しました:', error);
        setHasWikiData(false);
      } finally {
        setWikiLoading(false);
      }
    };
    fetchWikiSummary();
  }, [japaneseName, englishName]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    navigate(`/movie/${type}/${id}?page=${value}`);
  };

  const displayName = japaneseName || englishName;

  const getTypeLabel = () => {
    switch (type) {
      case 'company': return '（制作会社）';
      case 'collection': return '（シリーズ）';
      case 'person': return '';
      default: return '';
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${displayName} の映画一覧 | PULSE データベース`}</title>
      </Helmet>
      <Typography variant="h5" component="h1" gutterBottom>
        {displayName}
        {japaneseName && englishName && (
          <Typography
            component="span"
            sx={{
              ml: 1,
              fontSize: '0.8rem',
              color: 'text.secondary',
              fontWeight: 'normal',
              opacity: 0.8,
              verticalAlign: 'middle',
            }}
          >
            {englishName}
          </Typography>
        )}
        {getTypeLabel() && (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              fontSize: '0.7rem',
              color: 'text.secondary',
              opacity: 0.7,
              fontWeight: 'normal',
              verticalAlign: 'middle',
            }}
          >
            {getTypeLabel()}
          </Typography>
        )}
        <Typography
          component="span"
          sx={{
            ml: 1,
            fontSize: '0.8rem',
            color: 'text.secondary',
            opacity: 0.7,
            fontWeight: 'normal',
            verticalAlign: 'middle',
          }}
        >
          {totalCount > 0 && `• ${totalCount}作品`}
        </Typography>
      </Typography>

      <Grid container>
        <Grid item xs={12} md={8}>
          <Box sx={{ position: 'relative', mb: 3, minHeight: 60 }}>
            {wikiLoading ? (
              <>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </>
            ) : wikiSummary ? (
              <Expandable maxLines={3} expandButtonStyle="icon" iconSize={18} iconPadding={4} iconColor="#ffffff" iconBgColor="rgba(0, 0, 0, 0.9)">
                <Typography
                  variant="body2"
                  paragraph
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    mb: 0
                  }}
                >
                  {wikiSummary}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      ml: 1,
                      fontSize: 'inherit',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => setWikiOpen(true)}
                  >
                    詳細を見る
                  </Typography>
                </Typography>
              </Expandable>
            ) : (
              <Typography
                variant="body2"
                paragraph
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mb: 0,
                  opacity: 0.5
                }}
              >
                概要情報なし
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <LoadingPlaceholder type="card" count={ITEMS_PER_PAGE} />
      ) : (
        <>
          <Grid container spacing={{ xs: 1, sm: 1.5 }}>
            {movies.map((movie, index) => (
              <React.Fragment key={movie.movie_id}>
                <Grid item xs={4} sm={3} md={2}>
                  <MovieCard
                    movie={movie}
                    initialStatus={movieStatuses[movie.movie_id]}
                  />
                </Grid>
                {isMobile && index === 11 && (
                  <Grid item xs={12}>
                    <AdvertisementResponsive />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>

          {isMobile ? <RectangleAdvertisement /> : <Advertisement />}

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}

      <WikipediaViewer
        open={wikiOpen}
        onClose={() => setWikiOpen(false)}
        title={japaneseName || englishName}
        gameId={parseInt(id!, 10)}
      />
    </>
  );
};

export default MovieListByCategory;
